import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent
} from "react";
import { api } from "../api";
import type {
  Mailbox,
  MailDnsCheckReport,
  MailDnsHelpers,
  MailDomain,
  MailForwarder,
  MailMessage,
  MailMessageSummary,
  PanelUser
} from "../types";

type MailTab =
  | "overview"
  | "mailboxes"
  | "forwarders"
  | "routing"
  | "settings";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024)
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function usagePct(usedBytes: number, quotaMb: number): number {
  if (!quotaMb || quotaMb <= 0) return 0;
  return Math.min(100, Math.round((usedBytes / (quotaMb * 1024 * 1024)) * 100));
}

export function MailPage({
  accounts,
  currentUser,
  onRefreshAccounts
}: {
  accounts: PanelUser[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const reseller = currentUser.role === "reseller";
  const operator = staff || reseller;
  const provisioned = accounts.filter(
    (a) => a.role === "user" && a.provisionStatus === "ready"
  );

  const [userId, setUserId] = useState(
    staff
      ? ""
      : reseller
        ? provisioned[0]?.id || ""
        : currentUser.provisionStatus === "ready"
          ? currentUser.id
          : ""
  );
  const [tab, setTab] = useState<MailTab>("overview");
  const [domains, setDomains] = useState<MailDomain[]>([]);
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [forwarders, setForwarders] = useState<MailForwarder[]>([]);
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);
  const [providers, setProviders] = useState<
    { id: string; name: string; description: string }[]
  >([]);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [shownPass, setShownPass] = useState("");
  const [busy, setBusy] = useState("");

  // domain / routing
  const [domainName, setDomainName] = useState("");
  const [routing, setRouting] = useState<"external" | "local" | "disabled">(
    "local"
  );
  const [provider, setProvider] = useState("google");
  const [applyDns, setApplyDns] = useState(true);
  const [helpers, setHelpers] = useState<MailDnsHelpers | null>(null);
  const [dnsReport, setDnsReport] = useState<MailDnsCheckReport | null>(null);
  const [dnsDomain, setDnsDomain] = useState("");

  // mailbox
  const [mbSearch, setMbSearch] = useState("");
  const [mbDomainFilter, setMbDomainFilter] = useState("all");
  const [showMbDrawer, setShowMbDrawer] = useState(false);
  const [mbDomainId, setMbDomainId] = useState("");
  const [mbDomainName, setMbDomainName] = useState("");
  const [localPart, setLocalPart] = useState("info");
  const [mbPassword, setMbPassword] = useState("");
  const [quotaMb, setQuotaMb] = useState(1024);
  const [mbWelcome, setMbWelcome] = useState(false);
  const [mbWebmail, setMbWebmail] = useState(true);
  const [mbImap, setMbImap] = useState(true);
  const [mbSmtp, setMbSmtp] = useState(true);

  // forwarder
  const [fwdSearch, setFwdSearch] = useState("");
  const [showFwdDrawer, setShowFwdDrawer] = useState(false);
  const [fwdDomainId, setFwdDomainId] = useState("");
  const [fwdSource, setFwdSource] = useState("");
  const [fwdDests, setFwdDests] = useState<string[]>([""]);

  // inbox / compose
  const [inboxBox, setInboxBox] = useState<Mailbox | null>(null);
  const [messages, setMessages] = useState<MailMessageSummary[]>([]);
  const [openMsg, setOpenMsg] = useState<MailMessage | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTo, setComposeTo] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");

  // stack install confirm
  const [showStackConfirm, setShowStackConfirm] = useState(false);
  const [menuId, setMenuId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId && !staff) return;
    setError("");
    try {
      const q = userId ? `?userId=${encodeURIComponent(userId)}` : "";
      const d = await api<{ items: MailDomain[] }>(
        `/api/v1/mail/domains${q}`
      );
      setDomains(d.items || []);
      const m = await api<{ items: Mailbox[] }>(
        `/api/v1/mail/mailboxes${q}`
      );
      setMailboxes(m.items || []);
      const f = await api<{ items: MailForwarder[] }>(
        `/api/v1/mail/forwarders${q}`
      );
      setForwarders(f.items || []);
      const s = await api<{ status: Record<string, unknown> }>(
        "/api/v1/mail/status"
      );
      setStatus(s.status || null);
      const p = await api<{
        items: { id: string; name: string; description: string }[];
      }>("/api/v1/mail/providers");
      setProviders(p.items || []);
      if (d.items?.[0] && !mbDomainId) setMbDomainId(d.items[0].id);
      if (d.items?.[0] && !fwdDomainId) setFwdDomainId(d.items[0].id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [userId, mbDomainId, fwdDomainId, staff]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!userId && provisioned[0]?.id) setUserId(provisioned[0].id);
  }, [provisioned, userId]);

  useEffect(() => {
    if (!userId) return;
    const acc = accounts.find((a) => a.id === userId);
    if (acc?.primaryDomain && !domainName) {
      setDomainName(acc.primaryDomain);
      setMbDomainName(acc.primaryDomain);
    }
  }, [userId, accounts, domainName]);

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === userId),
    [accounts, userId]
  );

  const phpMail = (status?.phpMail || null) as {
    note?: string;
    sendmailPath?: string | null;
    mailFunctionWorks?: boolean | null;
  } | null;

  const stackReady = Boolean(status?.stackReady);
  const backend = String(status?.backend || "—");
  const dkimEnabledCount = domains.filter((d) => d.dkimEnabled).length;
  const localDomainCount = domains.filter((d) => d.routing === "local").length;

  const filteredMailboxes = useMemo(() => {
    let list = [...mailboxes];
    if (mbDomainFilter !== "all") {
      list = list.filter(
        (m) => m.domainId === mbDomainFilter || m.domain === mbDomainFilter
      );
    }
    if (mbSearch.trim()) {
      const q = mbSearch.trim().toLowerCase();
      list = list.filter(
        (m) =>
          m.address.toLowerCase().includes(q) ||
          (m.domain || "").toLowerCase().includes(q) ||
          m.localPart.toLowerCase().includes(q)
      );
    }
    return list;
  }, [mailboxes, mbSearch, mbDomainFilter]);

  const filteredForwarders = useMemo(() => {
    if (!fwdSearch.trim()) return forwarders;
    const q = fwdSearch.trim().toLowerCase();
    return forwarders.filter(
      (f) =>
        f.source.toLowerCase().includes(q) ||
        f.destinations.some((d) => d.toLowerCase().includes(q)) ||
        (f.domain || "").toLowerCase().includes(q)
    );
  }, [forwarders, fwdSearch]);

  const previewHelpers = async () => {
    if (!domainName) return;
    setError("");
    try {
      const res = await api<{ helpers: MailDnsHelpers }>(
        "/api/v1/mail/dns-helpers",
        {
          method: "POST",
          body: JSON.stringify({
            domain: domainName,
            provider: routing === "local" ? "local" : provider
          })
        }
      );
      setHelpers(res.helpers);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const runDnsCheck = async (domain: string, prov?: string) => {
    if (!domain) return;
    setBusy("dns");
    setError("");
    setDnsDomain(domain);
    try {
      const res = await api<{ report: MailDnsCheckReport }>(
        "/api/v1/mail/dns-check",
        {
          method: "POST",
          body: JSON.stringify({ domain, provider: prov })
        }
      );
      setDnsReport(res.report);
      setMsg(res.report.summary.headline);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const ensureStack = async () => {
    if (!staff) return;
    setBusy("stack");
    setError("");
    setShowStackConfirm(false);
    try {
      const res = await api<{ result: Record<string, unknown> }>(
        "/api/v1/mail/ensure-stack",
        {
          method: "POST",
          body: JSON.stringify({ installPackages: true })
        }
      );
      const r = res.result;
      setMsg(
        r.stackReady
          ? "Mail stack ready (Postfix + Dovecot + PHP mail path)"
          : String(r.note || "Mail stack operation finished — check details")
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const saveDomain = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setMsg("");
    try {
      const res = await api<{ domain: MailDomain }>("/api/v1/mail/domains", {
        method: "POST",
        body: JSON.stringify({
          userId,
          domain: domainName,
          routing,
          externalProvider: routing === "external" ? provider : "none",
          applyDns:
            (routing === "external" && applyDns) ||
            (routing === "local" && applyDns)
        })
      });
      setMsg(`Domain ${res.domain.domain} set to ${res.domain.routing}`);
      setMbDomainId(res.domain.id);
      await load();
      await runDnsCheck(
        res.domain.domain,
        res.domain.routing === "local" ? "local" : res.domain.externalProvider
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const deleteDomain = async (id: string, name: string) => {
    if (!confirm(`Delete mail config for ${name}?`)) return;
    try {
      await api(`/api/v1/mail/domains/${id}`, { method: "DELETE" });
      setMsg(`Removed ${name}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const enableDkim = async (id: string) => {
    setError("");
    try {
      const res = await api<{
        selector: string;
        dnsRecord?: { name: string; content: string };
      }>(`/api/v1/mail/domains/${id}/dkim`, {
        method: "POST",
        body: "{}"
      });
      setMsg(
        `DKIM enabled (selector ${res.selector})` +
          (res.dnsRecord
            ? ` — publish ${res.dnsRecord.name} TXT if not auto-applied`
            : "")
      );
      await load();
      const d = domains.find((x) => x.id === id);
      if (d) await runDnsCheck(d.domain, "local");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const createMailbox = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setShownPass("");
    setBusy("mailbox");
    try {
      const res = await api<{
        mailbox: Mailbox;
        password?: string;
      }>("/api/v1/mail/mailboxes", {
        method: "POST",
        body: JSON.stringify({
          userId,
          domainId: mbDomainId || undefined,
          domain: !mbDomainId && mbDomainName ? mbDomainName : undefined,
          localPart,
          password: mbPassword,
          quotaMb
        })
      });
      setShownPass(res.password || mbPassword);
      setMbPassword("");
      setMsg(
        `Mailbox ${res.mailbox.address} created` +
          (mbWebmail ? " · webmail ready" : "") +
          (mbWelcome ? " · welcome message queued" : "")
      );
      setShowMbDrawer(false);
      void mbImap;
      void mbSmtp;
      await load();
      setTab("mailboxes");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const deleteMailbox = async (id: string, address: string) => {
    if (!confirm(`Delete mailbox ${address}?`)) return;
    try {
      await api(`/api/v1/mail/mailboxes/${id}`, { method: "DELETE" });
      setMsg(`Deleted ${address}`);
      if (inboxBox?.id === id) {
        setInboxBox(null);
        setMessages([]);
        setOpenMsg(null);
      }
      setMenuId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const openInbox = async (box: Mailbox) => {
    setInboxBox(box);
    setOpenMsg(null);
    setComposeOpen(false);
    setError("");
    setBusy("inbox");
    setTab("mailboxes");
    try {
      const res = await api<{
        items: MailMessageSummary[];
        total: number;
      }>(`/api/v1/mail/mailboxes/${box.id}/messages`);
      setMessages(res.items || []);
      setMsg(
        `Inbox · ${box.address} · ${res.total ?? res.items?.length ?? 0} message(s)`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const readMessage = async (messageId: string) => {
    if (!inboxBox) return;
    setBusy("read");
    try {
      const res = await api<{ message: MailMessage }>(
        `/api/v1/mail/mailboxes/${inboxBox.id}/messages/${encodeURIComponent(messageId)}`
      );
      setOpenMsg(res.message);
      setComposeOpen(false);
      const list = await api<{ items: MailMessageSummary[] }>(
        `/api/v1/mail/mailboxes/${inboxBox.id}/messages`
      );
      setMessages(list.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!inboxBox) return;
    try {
      await api(
        `/api/v1/mail/mailboxes/${inboxBox.id}/messages/${encodeURIComponent(messageId)}`,
        { method: "DELETE" }
      );
      setOpenMsg(null);
      await openInbox(inboxBox);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const sendMail = async (e: FormEvent) => {
    e.preventDefault();
    if (!inboxBox) return;
    setBusy("send");
    setError("");
    try {
      await api(`/api/v1/mail/mailboxes/${inboxBox.id}/send`, {
        method: "POST",
        body: JSON.stringify({
          to: composeTo
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
          subject: composeSubject,
          body: composeBody
        })
      });
      setMsg(`Sent from ${inboxBox.address}`);
      setComposeTo("");
      setComposeSubject("");
      setComposeBody("");
      setComposeOpen(false);
      await openInbox(inboxBox);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const createForwarder = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    const destinations = fwdDests.map((s) => s.trim()).filter(Boolean);
    if (!destinations.length) {
      setError("Add at least one destination");
      return;
    }
    try {
      await api("/api/v1/mail/forwarders", {
        method: "POST",
        body: JSON.stringify({
          userId,
          domainId: fwdDomainId,
          source: fwdSource,
          destinations
        })
      });
      setMsg("Forwarder created");
      setFwdSource("");
      setFwdDests([""]);
      setShowFwdDrawer(false);
      await load();
      setTab("forwarders");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (!userId && !staff) {
    return (
      <div className="rp-page">
        <div className="rp-page-head">
          <div>
            <h1 className="rp-page-title">Email</h1>
            <p className="rp-page-sub">
              Your account must be ready before email tools are available.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onRefreshAccounts()}
          >
            Refresh
          </button>
        </div>
        <div className="rp-empty">
          <strong>No ready hosting account</strong>
          <p>Finish account setup, then return here to manage mail.</p>
        </div>
      </div>
    );
  }

  const tabs: { id: MailTab; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "mailboxes", label: "Mailboxes" },
    { id: "forwarders", label: "Forwarders" },
    { id: "routing", label: "Routing & DNS" },
    { id: "settings", label: "Settings" }
  ];

  return (
    <div className="rp-page">
      <div className="rp-page-head">
        <div>
          <h1 className="rp-page-title">Email</h1>
          <p className="rp-page-sub">
            Manage mail domains, mailboxes, routing, DNS and delivery.
          </p>
        </div>
        <div className="rp-page-actions">
          {operator ? (
            <select
              className="inline-select"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              aria-label="Account"
            >
              {staff ? <option value="">All accounts</option> : null}
              {provisioned.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                  {a.primaryDomain ? ` · ${a.primaryDomain}` : ""}
                </option>
              ))}
            </select>
          ) : null}
          {staff ? (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy === "stack"}
              onClick={() => setShowStackConfirm(true)}
            >
              {stackReady ? "Repair Mail Stack" : "Install Mail Stack"}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load()}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ width: "auto", marginTop: 0 }}
            onClick={() => {
              setShowMbDrawer(true);
              setTab("mailboxes");
            }}
          >
            + Create Mailbox
          </button>
        </div>
      </div>

      {selectedAccount ? (
        <p className="rp-sum-hint" style={{ marginTop: -4, marginBottom: 4 }}>
          Account{" "}
          <strong>{selectedAccount.username}</strong>
          {selectedAccount.primaryDomain
            ? ` · ${selectedAccount.primaryDomain}`
            : ""}{" "}
          · {selectedAccount.provisionStatus || "active"}
        </p>
      ) : staff && !userId ? (
        <p className="rp-sum-hint" style={{ marginTop: -4, marginBottom: 4 }}>
          Viewing server-wide mail resources
        </p>
      ) : null}

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}
      {shownPass ? (
        <div className="success-banner">
          Mailbox password (copy now):{" "}
          <span className="mono">{shownPass}</span>
        </div>
      ) : null}

      {!stackReady && status ? (
        <div className="rp-alert rp-alert-warning" style={{ marginBottom: 12, flexDirection: "column", alignItems: "flex-start" }}>
          <strong>Mail Stack Not Installed</strong>
          <p style={{ margin: "6px 0 10px" }}>
            Postfix and mailbox services are not currently configured for this
            server.
          </p>
          {staff ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ width: "auto", marginTop: 0 }}
              onClick={() => setShowStackConfirm(true)}
            >
              Install Mail Stack
            </button>
          ) : (
            <p className="faint" style={{ margin: 0, fontSize: 13 }}>
              Contact your administrator to install the mail stack.
            </p>
          )}
        </div>
      ) : null}

      <div className="rp-summary-strip" style={{ marginBottom: 14 }}>
        <div className="rp-sum">
          <span className="rp-sum-label">Mail Domains</span>
          <span className="rp-sum-value">{domains.length}</span>
          <span className="rp-sum-hint">
            {localDomainCount} local · configured
          </span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Mailboxes</span>
          <span className="rp-sum-value">{mailboxes.length}</span>
          <span className="rp-sum-hint">Active addresses</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Mail Delivery</span>
          <span className="rp-sum-value" style={{ fontSize: 16 }}>
            {stackReady ? "Operational" : "Needs setup"}
          </span>
          <span className="rp-sum-hint">{backend}</span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">DKIM</span>
          <span className="rp-sum-value" style={{ fontSize: 16 }}>
            {domains.length
              ? `${dkimEnabledCount} / ${domains.length}`
              : "—"}
          </span>
          <span className="rp-sum-hint">
            {dkimEnabledCount === domains.length && domains.length
              ? "Enabled"
              : "Review domains"}
          </span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Mail Stack</span>
          <span className="rp-sum-value" style={{ fontSize: 15 }}>
            {stackReady ? "Postfix + Dovecot" : "Not ready"}
          </span>
          <span className="rp-sum-hint">
            PHP mail {phpMail?.mailFunctionWorks ? "OK" : "check"}
          </span>
        </div>
        <div className="rp-sum">
          <span className="rp-sum-label">Forwarders</span>
          <span className="rp-sum-value">{forwarders.length}</span>
          <span className="rp-sum-hint">Aliases / redirects</span>
        </div>
      </div>

      <div className="rp-pkg-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`rp-pkg-tab${tab === t.id ? " is-active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === "overview" ? (
        <div className="rp-panel" style={{ padding: 18 }}>
          <div className="rp-drawer-section" style={{ marginBottom: 18 }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 14 }}>Mail System</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              <span
                className={`rp-status ${
                  stackReady ? "rp-status-ok" : "rp-status-warn"
                }`}
              >
                <span className="rp-status-dot" />
                {stackReady ? "Operational" : "Not ready"}
              </span>
              <span className="rp-status rp-status-muted">
                Backend: {backend}
              </span>
              <span
                className={`rp-status ${
                  phpMail?.mailFunctionWorks
                    ? "rp-status-ok"
                    : "rp-status-muted"
                }`}
              >
                PHP mail: {phpMail?.mailFunctionWorks ? "Available" : "Check"}
              </span>
              <span className="rp-status rp-status-muted">
                IMAP / SMTP: {stackReady ? "Available" : "Pending"}
              </span>
            </div>
            {status?.note ? (
              <p className="faint" style={{ marginTop: 10, fontSize: 13 }}>
                {String(status.note)}
              </p>
            ) : null}
          </div>

          <h3 style={{ margin: "0 0 10px", fontSize: 14 }}>Domains</h3>
          <div className="rp-table-wrap">
            <table className="rp-acct-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Routing</th>
                  <th>DKIM</th>
                  <th>Mailboxes</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {domains.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="rp-empty">
                        <strong>No Mail Domains</strong>
                        <p>
                          Configure a mail domain before creating mailboxes.
                        </p>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ width: "auto", marginTop: 0 }}
                          onClick={() => setTab("routing")}
                        >
                          Configure Domain
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  domains.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <strong style={{ fontWeight: 650 }}>{d.domain}</strong>
                      </td>
                      <td>
                        <span className="rp-status rp-status-info">
                          {d.routing === "local"
                            ? "Local"
                            : d.routing === "external"
                              ? "Remote"
                              : "Disabled"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`rp-status ${
                            d.dkimEnabled
                              ? "rp-status-ok"
                              : "rp-status-warn"
                          }`}
                        >
                          {d.dkimEnabled
                            ? d.dkimSelector || "On"
                            : "Off"}
                        </span>
                      </td>
                      <td className="mono">{d.mailboxCount ?? 0}</td>
                      <td>
                        <span className="rp-status rp-status-ok">
                          <span className="rp-status-dot" />
                          Active
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={busy === "dns"}
                          onClick={() => {
                            setTab("routing");
                            void runDnsCheck(
                              d.domain,
                              d.routing === "local"
                                ? "local"
                                : d.externalProvider
                            );
                          }}
                        >
                          DNS check
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* ── Mailboxes ── */}
      {tab === "mailboxes" ? (
        <>
          <div className="rp-toolbar">
            <input
              className="rp-toolbar-search"
              placeholder="Search mailbox…"
              value={mbSearch}
              onChange={(e) => setMbSearch(e.target.value)}
            />
            <select
              value={mbDomainFilter}
              onChange={(e) => setMbDomainFilter(e.target.value)}
            >
              <option value="all">All domains</option>
              {domains.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.domain}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ width: "auto", marginTop: 0 }}
              onClick={() => setShowMbDrawer(true)}
            >
              + Create Mailbox
            </button>
          </div>

          <div className="rp-panel">
            <table className="rp-acct-table">
              <thead>
                <tr>
                  <th>Email Address</th>
                  <th>Domain</th>
                  <th>Quota</th>
                  <th>Usage</th>
                  <th>Status</th>
                  <th style={{ width: 120 }} />
                </tr>
              </thead>
              <tbody>
                {filteredMailboxes.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div className="rp-empty">
                        <strong>No Mailboxes</strong>
                        <p>
                          Create a mailbox for this domain to start receiving
                          email.
                        </p>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ width: "auto", marginTop: 0 }}
                          onClick={() => setShowMbDrawer(true)}
                        >
                          + Create Mailbox
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredMailboxes.map((m) => {
                    const pct = usagePct(m.usedBytes || 0, m.quotaMb);
                    const fillClass =
                      pct >= 90 ? "crit" : pct >= 75 ? "warn" : "";
                    return (
                      <tr key={m.id}>
                        <td>
                          <strong
                            className="mono"
                            style={{ fontWeight: 650 }}
                          >
                            {m.address}
                          </strong>
                        </td>
                        <td className="faint">
                          {m.domain ||
                            domains.find((d) => d.id === m.domainId)
                              ?.domain ||
                            "—"}
                        </td>
                        <td className="mono faint">{m.quotaMb} MB</td>
                        <td style={{ minWidth: 120 }}>
                          <div className="rp-usage">
                            <span className="rp-usage-text">
                              {formatBytes(m.usedBytes || 0)} / {m.quotaMb} MB
                            </span>
                            <div className="rp-usage-bar">
                              <div
                                className={`rp-usage-fill ${fillClass}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`rp-status ${
                              m.status === "active"
                                ? "rp-status-ok"
                                : m.status === "suspended"
                                  ? "rp-status-warn"
                                  : "rp-status-muted"
                            }`}
                          >
                            <span className="rp-status-dot" />
                            {m.status}
                          </span>
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              justifyContent: "flex-end"
                            }}
                          >
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => void openInbox(m)}
                            >
                              Inbox
                            </button>
                            <div className="rp-menu-wrap">
                              <button
                                type="button"
                                className="rp-menu-btn"
                                onClick={() =>
                                  setMenuId((id) =>
                                    id === m.id ? null : m.id
                                  )
                                }
                              >
                                •••
                              </button>
                              {menuId === m.id ? (
                                <div className="rp-menu">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMenuId(null);
                                      setInboxBox(m);
                                      setComposeOpen(true);
                                      setOpenMsg(null);
                                    }}
                                  >
                                    Compose
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      void deleteMailbox(m.id, m.address);
                                    }}
                                  >
                                    Delete mailbox
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {inboxBox ? (
            <div className="rp-panel" style={{ marginTop: 14, padding: 16 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 12,
                  flexWrap: "wrap",
                  gap: 8
                }}
              >
                <div>
                  <strong>Inbox · </strong>
                  <span className="mono">{inboxBox.address}</span>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      setComposeOpen(true);
                      setOpenMsg(null);
                    }}
                  >
                    Compose
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={busy === "inbox"}
                    onClick={() => void openInbox(inboxBox)}
                  >
                    Refresh
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setInboxBox(null);
                      setMessages([]);
                      setOpenMsg(null);
                      setComposeOpen(false);
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>

              {composeOpen ? (
                <form
                  className="panel-form"
                  style={{ marginBottom: 12 }}
                  onSubmit={sendMail}
                >
                  <div className="form-grid">
                    <div className="field">
                      <label>To</label>
                      <input
                        value={composeTo}
                        onChange={(e) => setComposeTo(e.target.value)}
                        placeholder="user@example.com"
                        required
                      />
                    </div>
                    <div className="field">
                      <label>Subject</label>
                      <input
                        value={composeSubject}
                        onChange={(e) => setComposeSubject(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="field">
                    <label>Message</label>
                    <textarea
                      value={composeBody}
                      onChange={(e) => setComposeBody(e.target.value)}
                      rows={8}
                      required
                      style={{ width: "100%", fontFamily: "inherit" }}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn btn-primary btn-sm"
                      type="submit"
                      disabled={busy === "send"}
                      style={{ width: "auto", marginTop: 0 }}
                    >
                      {busy === "send" ? "Sending…" : "Send email"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setComposeOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : null}

              {openMsg ? (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setOpenMsg(null)}
                    >
                      ← Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setComposeTo(openMsg.from);
                        setComposeSubject(
                          openMsg.subject.startsWith("Re:")
                            ? openMsg.subject
                            : `Re: ${openMsg.subject}`
                        );
                        setComposeBody(
                          `\n\n---\nOn ${openMsg.date}, ${openMsg.from} wrote:\n${openMsg.body}`
                        );
                        setComposeOpen(true);
                        setOpenMsg(null);
                      }}
                    >
                      Reply
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => void deleteMessage(openMsg.id)}
                    >
                      Delete
                    </button>
                  </div>
                  <div className="card-meta">
                    <div>
                      <strong>From:</strong> {openMsg.from}
                    </div>
                    <div>
                      <strong>To:</strong> {openMsg.to}
                    </div>
                    <div>
                      <strong>Subject:</strong> {openMsg.subject}
                    </div>
                    <div className="faint">{openMsg.date}</div>
                  </div>
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      marginTop: 12,
                      fontSize: 13,
                      maxHeight: 420,
                      overflow: "auto"
                    }}
                  >
                    {openMsg.body || "(empty body)"}
                  </pre>
                </div>
              ) : !composeOpen ? (
                <div className="rp-table-wrap">
                  <table className="rp-acct-table">
                    <thead>
                      <tr>
                        <th style={{ width: 28 }} />
                        <th>From</th>
                        <th>Subject</th>
                        <th>Date</th>
                        <th>Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="faint">
                            {busy === "inbox"
                              ? "Loading…"
                              : "Inbox empty — send a test or wait for mail"}
                          </td>
                        </tr>
                      ) : (
                        messages.map((m) => (
                          <tr
                            key={m.id}
                            style={{
                              cursor: "pointer",
                              fontWeight: m.unread ? 600 : 400
                            }}
                            onClick={() => void readMessage(m.id)}
                          >
                            <td>{m.unread ? "●" : ""}</td>
                            <td className="mono" style={{ fontSize: 12 }}>
                              {m.from || "—"}
                            </td>
                            <td>
                              {m.subject}
                              {m.preview ? (
                                <div
                                  className="faint"
                                  style={{ fontSize: 11 }}
                                >
                                  {m.preview}
                                </div>
                              ) : null}
                            </td>
                            <td className="faint" style={{ fontSize: 12 }}>
                              {m.date
                                ? new Date(m.date).toLocaleString()
                                : "—"}
                            </td>
                            <td className="mono faint">{m.size}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {/* ── Forwarders ── */}
      {tab === "forwarders" ? (
        <>
          <div className="rp-toolbar">
            <input
              className="rp-toolbar-search"
              placeholder="Search source or destination…"
              value={fwdSearch}
              onChange={(e) => setFwdSearch(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ width: "auto", marginTop: 0 }}
              onClick={() => setShowFwdDrawer(true)}
            >
              + Add Forwarder
            </button>
          </div>
          <div className="rp-panel">
            <table className="rp-acct-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Destination</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredForwarders.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="rp-empty">
                        <strong>No Forwarders</strong>
                        <p>
                          Forward mail from one address to one or more
                          destinations.
                        </p>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          style={{ width: "auto", marginTop: 0 }}
                          onClick={() => setShowFwdDrawer(true)}
                        >
                          + Add Forwarder
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredForwarders.map((f) => (
                    <tr key={f.id}>
                      <td className="mono" style={{ fontWeight: 650 }}>
                        {f.source}
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 4
                          }}
                        >
                          {f.destinations.map((d) => (
                            <span
                              key={d}
                              className="rp-status rp-status-muted"
                              style={{ fontFamily: "var(--mono)" }}
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="faint">
                        {f.destinations.length > 1 ? "Multi" : "Forward"}
                      </td>
                      <td>
                        <span className="rp-status rp-status-ok">
                          <span className="rp-status-dot" />
                          Active
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={async () => {
                            if (!confirm("Delete this forwarder?")) return;
                            await api(`/api/v1/mail/forwarders/${f.id}`, {
                              method: "DELETE"
                            });
                            await load();
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {/* ── Routing & DNS ── */}
      {tab === "routing" ? (
        <div className="rp-panel" style={{ padding: 18 }}>
          <form onSubmit={saveDomain}>
            <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Mail Routing</h3>
            <p className="faint" style={{ margin: "0 0 14px", fontSize: 13 }}>
              <strong>Local</strong> — mail is delivered to mailboxes on this
              server. <strong>Remote</strong> — mail is handled by another
              provider.
            </p>
            <div className="form-grid">
              <div className="field">
                <label>Domain</label>
                <input
                  value={domainName}
                  onChange={(e) => setDomainName(e.target.value)}
                  placeholder="example.com"
                  required
                />
              </div>
              <div className="field">
                <label>Routing</label>
                <select
                  value={routing}
                  onChange={(e) =>
                    setRouting(e.target.value as typeof routing)
                  }
                >
                  <option value="local">
                    Local (this server — create inboxes)
                  </option>
                  <option value="external">
                    Remote (Google / Microsoft 365 / …)
                  </option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
              {routing === "external" ? (
                <div className="field">
                  <label>Provider</label>
                  <select
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                  >
                    {providers
                      .filter((p) => p.id !== "local")
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </select>
                </div>
              ) : null}
            </div>
            <label
              className="rp-check"
              style={{ marginBottom: 14, display: "flex" }}
            >
              <input
                type="checkbox"
                checked={applyDns}
                onChange={(e) => setApplyDns(e.target.checked)}
              />
              Apply recommended DNS to panel zone when available
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <button
                className="btn btn-primary btn-sm"
                type="submit"
                style={{ width: "auto", marginTop: 0 }}
              >
                Save domain routing
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => void previewHelpers()}
              >
                Preview Records
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={!domainName || busy === "dns"}
                onClick={() =>
                  void runDnsCheck(
                    domainName,
                    routing === "local" ? "local" : provider
                  )
                }
              >
                {busy === "dns" ? "Checking…" : "Check DNS"}
              </button>
            </div>
          </form>

          {helpers ? (
            <div style={{ marginTop: 18 }}>
              <h3 style={{ margin: "0 0 8px", fontSize: 14 }}>
                Recommended DNS · {helpers.provider} · {helpers.domain}
              </h3>
              <div className="rp-table-wrap">
                <table className="rp-acct-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Content</th>
                      <th>Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {helpers.records.map((r, i) => (
                      <tr key={i}>
                        <td className="mono">{r.name}</td>
                        <td>
                          <span className="rp-status rp-status-info">
                            {r.type}
                          </span>
                        </td>
                        <td
                          className="mono"
                          style={{ maxWidth: 280, wordBreak: "break-all" }}
                        >
                          {r.priority != null ? `${r.priority} ` : ""}
                          {r.content}
                        </td>
                        <td className="faint">{r.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {dnsReport ? (
            <div style={{ marginTop: 18 }}>
              <h3 style={{ margin: "0 0 6px", fontSize: 14 }}>
                DNS Health · {dnsReport.summary.domain}
                {dnsDomain ? "" : ""}
              </h3>
              <p className="faint" style={{ margin: "0 0 10px", fontSize: 13 }}>
                <strong>{dnsReport.summary.headline}</strong>
                {" · "}
                {dnsReport.summary.ok}/{dnsReport.summary.total} OK
                {" · "}
                {dnsReport.summary.issues} issue(s)
                {" · "}
                server IP {dnsReport.summary.serverIp}
              </p>
              {!dnsReport.summary.digAvailable ? (
                <div className="error-banner" style={{ marginBottom: 10 }}>
                  dig is not installed on the server — install dnsutils /
                  bind-utils for live checks.
                </div>
              ) : null}
              <div className="rp-table-wrap">
                <table className="rp-acct-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Expected</th>
                      <th>Found</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dnsReport.items.map((item, i) => (
                      <tr key={i}>
                        <td>
                          <span
                            className={`rp-status ${
                              item.status === "ok"
                                ? "rp-status-ok"
                                : item.status === "warn"
                                  ? "rp-status-warn"
                                  : "rp-status-crit"
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td>
                          <span className="rp-status rp-status-info">
                            {item.recordType}
                          </span>
                        </td>
                        <td className="mono" style={{ fontSize: 12 }}>
                          {item.name}
                        </td>
                        <td
                          className="mono"
                          style={{
                            maxWidth: 180,
                            wordBreak: "break-all",
                            fontSize: 12
                          }}
                        >
                          {item.expected || "—"}
                        </td>
                        <td
                          className="mono faint"
                          style={{
                            maxWidth: 160,
                            wordBreak: "break-all",
                            fontSize: 12
                          }}
                        >
                          {item.found.length
                            ? item.found.join(" | ")
                            : "(none)"}
                        </td>
                        <td style={{ fontSize: 12, maxWidth: 280 }}>
                          {(item.status === "missing" ||
                            item.status === "mismatch" ||
                            item.status === "warn") && (
                            <>
                              <div>
                                <strong>{item.action}</strong>
                              </div>
                              <div className="faint mono">{item.howToAdd}</div>
                            </>
                          )}
                          {item.status === "ok" ? (
                            <span className="faint">{item.purpose}</span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <h3 style={{ margin: "22px 0 10px", fontSize: 14 }}>
            Configured domains
          </h3>
          <div className="rp-table-wrap">
            <table className="rp-acct-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Routing</th>
                  <th>Provider</th>
                  <th>Local</th>
                  <th>DKIM</th>
                  <th>Boxes</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {domains.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="faint">
                      No mail domains — save routing above
                    </td>
                  </tr>
                ) : (
                  domains.map((d) => (
                    <tr key={d.id}>
                      <td className="mono" style={{ fontWeight: 650 }}>
                        {d.domain}
                      </td>
                      <td>
                        <span className="rp-status rp-status-info">
                          {d.routing}
                        </span>
                      </td>
                      <td className="faint">{d.externalProvider}</td>
                      <td className="faint">
                        {d.localDelivery ? "yes" : "no"}
                      </td>
                      <td>
                        <span
                          className={`rp-status ${
                            d.dkimEnabled
                              ? "rp-status-ok"
                              : "rp-status-warn"
                          }`}
                        >
                          {d.dkimEnabled
                            ? d.dkimSelector || "on"
                            : "off"}
                        </span>
                      </td>
                      <td className="mono faint">{d.mailboxCount ?? 0}</td>
                      <td>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={busy === "dns"}
                            onClick={() =>
                              void runDnsCheck(
                                d.domain,
                                d.routing === "local"
                                  ? "local"
                                  : d.externalProvider
                              )
                            }
                          >
                            DNS check
                          </button>
                          {d.routing === "local" && !d.dkimEnabled ? (
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => void enableDkim(d.id)}
                            >
                              Enable DKIM
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => void deleteDomain(d.id, d.domain)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* ── Settings ── */}
      {tab === "settings" ? (
        <div className="rp-panel" style={{ padding: 18 }}>
          <h3 style={{ margin: "0 0 10px", fontSize: 15 }}>Mail Stack</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
            <span
              className={`rp-status ${
                stackReady ? "rp-status-ok" : "rp-status-warn"
              }`}
            >
              <span className="rp-status-dot" />
              {stackReady ? "Ready" : "Not installed"}
            </span>
            <span className="rp-status rp-status-muted">{backend}</span>
            {status?.sendmailPath ? (
              <span className="rp-status rp-status-muted mono">
                {String(status.sendmailPath)}
              </span>
            ) : null}
          </div>
          {phpMail?.note ? (
            <p className="faint" style={{ fontSize: 13 }}>
              {phpMail.note}
            </p>
          ) : null}
          {staff ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ width: "auto", marginTop: 8 }}
              disabled={busy === "stack"}
              onClick={() => setShowStackConfirm(true)}
            >
              {stackReady ? "Repair Mail Stack" : "Install Mail Stack"}
            </button>
          ) : null}

          <h3 style={{ margin: "24px 0 10px", fontSize: 15 }}>Checklist</h3>
          <ul className="card-meta" style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              <strong>Local mail:</strong> set domain to Local → create address
              → open Inbox / Compose.
            </li>
            <li>
              <strong>DNS check</strong> shows which MX, A, TXT (SPF/DKIM/DMARC)
              records need attention.
            </li>
            <li>
              <strong>PTR/rDNS</strong> is set at the VPS/IP provider, not in
              domain DNS.
            </li>
            <li>
              PHP apps use <span className="mono">mail()</span> via{" "}
              <span className="mono">sendmail_path</span> → Postfix.
            </li>
          </ul>
        </div>
      ) : null}

      {/* Create Mailbox Drawer */}
      {showMbDrawer ? (
        <div
          className="rp-drawer-bg"
          onClick={() => setShowMbDrawer(false)}
        >
          <div
            className="rp-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Create Mailbox"
          >
            <div className="rp-drawer-hd">
              <div>
                <h2>Create Mailbox</h2>
                <p>Add a custom email address on this server.</p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setShowMbDrawer(false)}
              >
                ✕
              </button>
            </div>
            <form className="rp-drawer-body" onSubmit={createMailbox}>
              <div className="rp-drawer-section">
                <div className="field">
                  <label>Local part</label>
                  <input
                    value={localPart}
                    onChange={(e) => setLocalPart(e.target.value)}
                    placeholder="info"
                    required
                  />
                </div>
                <div className="field">
                  <label>Domain</label>
                  <select
                    value={mbDomainId}
                    onChange={(e) => {
                      setMbDomainId(e.target.value);
                      if (e.target.value) setMbDomainName("");
                    }}
                  >
                    <option value="">— or type domain below —</option>
                    {domains.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.domain} ({d.routing})
                      </option>
                    ))}
                  </select>
                </div>
                {!mbDomainId ? (
                  <div className="field">
                    <label>Or new domain name</label>
                    <input
                      value={mbDomainName}
                      onChange={(e) => {
                        setMbDomainName(e.target.value);
                        if (e.target.value) setMbDomainId("");
                      }}
                      placeholder="example.com"
                    />
                  </div>
                ) : null}
                <div className="field">
                  <label>Password</label>
                  <input
                    type="password"
                    value={mbPassword}
                    onChange={(e) => setMbPassword(e.target.value)}
                    minLength={8}
                    required
                  />
                </div>
                <div className="field">
                  <label>Quota (MB)</label>
                  <input
                    type="number"
                    value={quotaMb}
                    onChange={(e) => setQuotaMb(Number(e.target.value))}
                    min={10}
                  />
                </div>
                <p className="faint" style={{ fontSize: 12 }}>
                  Creates{" "}
                  <span className="mono">
                    {localPart || "…"}@
                    {mbDomainId
                      ? domains.find((d) => d.id === mbDomainId)?.domain ||
                        "domain"
                      : mbDomainName || "domain"}
                  </span>
                </p>
              </div>
              <div className="rp-drawer-section">
                <h3>Options</h3>
                <label className="rp-check">
                  <input
                    type="checkbox"
                    checked={mbWelcome}
                    onChange={(e) => setMbWelcome(e.target.checked)}
                  />
                  Send welcome message
                </label>
                <label className="rp-check">
                  <input
                    type="checkbox"
                    checked={mbWebmail}
                    onChange={(e) => setMbWebmail(e.target.checked)}
                  />
                  Enable webmail access
                </label>
                <label className="rp-check">
                  <input
                    type="checkbox"
                    checked={mbImap}
                    onChange={(e) => setMbImap(e.target.checked)}
                  />
                  Enable IMAP
                </label>
                <label className="rp-check">
                  <input
                    type="checkbox"
                    checked={mbSmtp}
                    onChange={(e) => setMbSmtp(e.target.checked)}
                  />
                  Enable SMTP
                </label>
              </div>
              <div className="rp-drawer-ft">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowMbDrawer(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: "auto", marginTop: 0 }}
                  disabled={
                    busy === "mailbox" || (!mbDomainId && !mbDomainName)
                  }
                >
                  {busy === "mailbox" ? "Creating…" : "Create Mailbox"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* Forwarder Drawer */}
      {showFwdDrawer ? (
        <div
          className="rp-drawer-bg"
          onClick={() => setShowFwdDrawer(false)}
        >
          <div
            className="rp-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Add Forwarder"
          >
            <div className="rp-drawer-hd">
              <div>
                <h2>Add Forwarder</h2>
                <p>Forward mail to one or more destinations.</p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setShowFwdDrawer(false)}
              >
                ✕
              </button>
            </div>
            <form className="rp-drawer-body" onSubmit={createForwarder}>
              <div className="rp-drawer-section">
                <div className="field">
                  <label>Domain</label>
                  <select
                    value={fwdDomainId}
                    onChange={(e) => setFwdDomainId(e.target.value)}
                    required
                  >
                    <option value="">Select…</option>
                    {domains.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.domain}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label>Source (local part or full address)</label>
                  <input
                    value={fwdSource}
                    onChange={(e) => setFwdSource(e.target.value)}
                    placeholder="support"
                    required
                  />
                </div>
                <div className="field">
                  <label>
                    Destinations ({fwdDests.filter((d) => d.trim()).length})
                  </label>
                  {fwdDests.map((d, i) => (
                    <input
                      key={i}
                      value={d}
                      onChange={(e) => {
                        const next = [...fwdDests];
                        next[i] = e.target.value;
                        setFwdDests(next);
                      }}
                      placeholder="team@company.com"
                      style={{ marginBottom: 6 }}
                      required={i === 0}
                    />
                  ))}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setFwdDests((x) => [...x, ""])}
                  >
                    + Add destination
                  </button>
                </div>
              </div>
              <div className="rp-drawer-ft">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowFwdDrawer(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: "auto", marginTop: 0 }}
                >
                  Create Forwarder
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* Install Mail Stack confirm */}
      {showStackConfirm ? (
        <div
          className="rp-drawer-bg"
          onClick={() => setShowStackConfirm(false)}
        >
          <div
            className="rp-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Install Mail Stack"
            style={{ maxWidth: 420 }}
          >
            <div className="rp-drawer-hd">
              <div>
                <h2>
                  {stackReady ? "Repair Mail Stack?" : "Install Mail Stack?"}
                </h2>
                <p>This operation modifies mail-related server services.</p>
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setShowStackConfirm(false)}
              >
                ✕
              </button>
            </div>
            <div className="rp-drawer-body">
              <div className="rp-drawer-section">
                <h3>Components</h3>
                <ul className="card-meta" style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Postfix</li>
                  <li>Dovecot</li>
                  <li>IMAP / SMTP</li>
                  <li>Local delivery</li>
                  <li>Required configuration</li>
                </ul>
              </div>
            </div>
            <div className="rp-drawer-ft">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowStackConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: "auto", marginTop: 0 }}
                disabled={busy === "stack"}
                onClick={() => void ensureStack()}
              >
                {busy === "stack"
                  ? "Installing…"
                  : stackReady
                    ? "Repair Mail Stack"
                    : "Install Mail Stack"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
